import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Box, Typography, Button } from '@mui/material';
import { ArrowBack, Print } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Session, SessionStatistics } from '@/types/session';
import { Opportunity } from '@/types/opportunity';
import { sessionStorage } from '@services/storage/session-storage';
import { opportunityStorage } from '@services/storage/opportunity-storage';
import { calculateStatistics } from '@services/calculations/statistics';
import { calculateCompliance } from '@services/calculations/compliance';
import { SessionSummary } from '@components/session/SessionSummary';
import { Loading } from '@components/common/Loading';
import { formatDate } from '@utils/date';
import { logger } from '@utils/logger';

interface SessionWithStats {
  session: Session;
  statistics: SessionStatistics;
  opportunities: Opportunity[];
}

export function PrintSummariesPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState<SessionWithStats[]>([]);
  const [loading, setLoading] = useState(true);

  const sessionIds = searchParams.get('ids')?.split(',').filter(Boolean) || [];

  useEffect(() => {
    if (sessionIds.length === 0) {
      navigate('/');
      return;
    }
    loadAllSessions();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const loadAllSessions = async () => {
    try {
      setLoading(true);
      const results: SessionWithStats[] = [];

      for (const id of sessionIds) {
        const session = await sessionStorage.getById(id);
        if (!session) continue;

        const opportunities = await opportunityStorage.getBySessionId(id);
        const stats = calculateStatistics(opportunities);
        const complianceRate = calculateCompliance(opportunities);

        results.push({
          session,
          statistics: { ...stats, complianceRate },
          opportunities,
        });
      }

      // Sort by session date descending
      results.sort(
        (a, b) =>
          b.session.sessionDate.getTime() - a.session.sessionDate.getTime(),
      );

      setData(results);
    } catch (error) {
      logger.error('Failed to load sessions for print:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading message={t('printSummaries.loading')} />;
  }

  if (data.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Typography variant="h6" color="text.secondary">
          {t('printSummaries.noSessions')}
        </Typography>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          {t('printSummaries.backToSessions')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      {/* Toolbar — hidden when printing */}
      <Box
        className="no-print"
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
        >
          {t('printSummaries.backToSessions')}
        </Button>
        <Button
          variant="contained"
          startIcon={<Print />}
          onClick={() => window.print()}
        >
          {t('sessionActions.print')}
        </Button>
      </Box>

      {/* Print header */}
      <Box sx={{ mb: 3 }} className="print-only">
        <Typography variant="body2" color="text.secondary">
          {t('home.printDate')}: {formatDate(new Date())}
        </Typography>
      </Box>

      {/* Session summaries */}
      {data.map((item, index) => (
        <Box
          key={item.session.sessionId}
          sx={{
            mb: 4,
            '@media print': {
              breakBefore: index > 0 ? 'page' : 'auto',
            },
          }}
        >
          <SessionSummary
            session={item.session}
            statistics={item.statistics}
          />
        </Box>
      ))}
    </Container>
  );
}
